import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    Signal,
    ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { TuiAlertModule, TuiAlertService, TuiButtonModule, TuiDialogContext, TuiDialogService } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT, PolymorpheusContent } from '@tinkoff/ng-polymorpheus';
import { OrdersActions, selectLoading, selectOrder } from '@us/web/orders/data-access';
import { convertPaymentType, Order, OrderState, PaymentType } from '@us/web/shared/data-access';
import { FormatCurrencyPipe, selectPaymentSettings } from '@us/web/shop/data-access';
import { PusherService } from '@us/web/websockets/data-access';
import { QRCodeModule } from 'angularx-qrcode';
import { filter, tap } from 'rxjs';

@Component({
    selector: 'us-qrpayment',
    templateUrl: './qrpayment.component.html',
    styleUrls: ['./qrpayment.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [QRCodeModule, TranslocoModule, FormatCurrencyPipe, TuiButtonModule, TuiAlertModule]
})
export class QrPaymentComponent implements OnInit {
    @ViewChild('qrcodeCanvas', { static: false }) qrcodeCanvas!: any;
    readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
        boolean,
        {
            payment_detail: {
                code: number;
                message: string;
                data?: {
                    qr_payment?: string;
                    orderId?: string;
                    amount?: number | string;
                    description?: string;
                };
            };
            order: Order;
        }
    >;
    readonly route = inject(ActivatedRoute);
    readonly destroyRef = inject(DestroyRef);
    readonly store = inject(Store);
    readonly pusherService = inject(PusherService);
    readonly translocoService = inject(TranslocoService);
    private readonly alertService = inject(TuiAlertService);
    readonly dialogService = inject(TuiDialogService);
    readonly PaymentType = PaymentType;
    readonly paymentSettings = this.store.selectSignal(selectPaymentSettings);
    readonly loading: Signal<boolean> = this.store.selectSignal(selectLoading);
    isFirst = true;

    width = 300;

    paymentDetail = computed(() => {
        return this.context.data.payment_detail?.data;
    });

    orderId = computed(() => {
        return this.order().order_id;
    });

    order = computed(() => {
        return this.context.data.order;
    });

    qrcodeStr = computed(() => {
        return this.paymentDetail()?.qr_payment || '';
    });

    orderCode = computed(() => {
        const orderId = this.paymentDetail()?.orderId || this.order().order_id;
        return orderId;
    });

    amount = computed(() => {
        const amount = this.paymentDetail()?.amount || this.order().grand_total;
        return typeof amount === 'number' ? amount : parseFloat(amount);
    });

    description = computed(() => {
        const description = this.paymentDetail()?.description;
        if (description) {
            return description;
        }
        return this.translocoService.translate('payment_order', { order_code: this.orderCode() });
    });

    paymentType = computed(() => {
        return (this.order().payment_type || '') as PaymentType;
    });

    infoBank = computed(() => {
        const payment_setting = this.paymentSettings().find(
            (item: { e_wallet_type: number }) => item.e_wallet_type === convertPaymentType[this.paymentType()]
        );
        return payment_setting;
    });

    ngOnInit(): void {
        this.store
            .select(selectOrder(this.orderId() as string))
            .pipe(
                filter(order => order !== null && !this.isFirst),
                takeUntilDestroyed(this.destroyRef),
                tap(order => {
                    if (!order) {
                        return;
                    }
                    if ([OrderState.PaymentFailed, OrderState.PaymentSuccess].includes(order.current_state)) {
                        if (order.current_state === OrderState.PaymentFailed) {
                            this.alertService
                                .open(this.translocoService.translate('order_state.payment_failed'), {
                                    status: 'error'
                                })
                                .subscribe();
                            // chỉ hiện lỗi chứ không đóng tự động
                            // this.context.completeWith(false);
                        } else {
                            this.alertService
                                .open(this.translocoService.translate('order_state.payment_success'), {
                                    status: 'success'
                                })
                                .subscribe();
                            this.context.completeWith(true);
                        }
                    } else {
                        this.alertService
                            .open(this.translocoService.translate('order_pending_payment'), {
                                status: 'warning'
                            })
                            .subscribe();
                    }
                })
            )
            .subscribe();
        this.isFirst = false;
    }

    downloadQRCode(): void {
        let imageData = this.qrcodeStr();
        if (this.order().payment_type === PaymentType.Mpos) {
            if (this.qrcodeCanvas && this.qrcodeCanvas.qrcElement) {
                const qrCanvas = this.qrcodeCanvas.qrcElement.nativeElement.querySelector(
                    'canvas'
                ) as HTMLCanvasElement;
                imageData = qrCanvas.toDataURL('image/png');
            }
        }
        const link = document.createElement('a');
        link.href = imageData;
        link.download = 'qrcode_payment.png';
        link.click();
    }

    checkTransaction(): void {
        this.store.dispatch(OrdersActions.loadOrderPaymentStatus({ id: this.order().order_id }));
    }

    cancel() {
        this.context.completeWith(false);
    }

    openDiaLogConfirm(content: PolymorpheusContent<TuiDialogContext>) {
        this.dialogService
            .open(content, {
                label: this.translocoService.translate('are_you_sure_you_want_to_exit_payment'),
                size: 'm',
                dismissible: false,
                closeable: false
            })
            .subscribe();
    }
}
